import {
  CONVERSION_EVENT_METRICS,
  MetricCategory,
  MetricIdentifier,
  NA,
  metricsMediumDefaults,
  metricsMediumWinningCriteriaDefaults,
} from 'modules/experiments/constants';
import {
  EXPORT_TABLE_DEFAULT_HEADER,
  ExperimentAnalyticsTranslation,
  ExperimentHoldoutBreakdownDetail,
  ExperimentTableDefaultExportData,
  ExperimentTableDefaultExportHeaders,
  ExperimentVariantBreakdownArray,
  ExperimentVariantBreakdownDetail,
  MetricOption,
  TABLE_EXPORT,
} from 'modules/experiments/types';
import { AnalyticsMetric, MessageMedium } from 'modules/graphql';
import { LabelKeyObject } from 'react-csv/components/CommonPropTypes';
import { TFunction } from 'react-i18next';
import { getVariationTemplateLink } from 'utils/appRoutes';
import { downloadAsCsvFileWithHeaders } from 'utils/csv';
import { localizeNumber } from 'utils/l10n';
import { printUniqueMetricName, retrieveMetricResult, tFunctionWithSource } from './common';
import { isExperimentHoldoutRow } from './ruleEngine';
import { getTestingSubjectText } from './table';

type Headers = LabelKeyObject;
type TableExportActionArgs = {
  experimentId: number;
  experimentName: string;
  csvData: ExperimentTableDefaultExportData[];
  allMetricsHeaders: Headers[];
  currentViewHeaders: Headers[];
};

type TableDefaultAndOptionalExportHeaders = {
  defaultHeaders: ExperimentTableDefaultExportHeaders[];
  optionalHeaders: Headers[];
};

type TableDisplayHeadersArgs = TableDefaultAndOptionalExportHeaders & {
  selectedMetricsColumn: string[];
};

type TableDisplayHeaders = {
  allMetricsHeaders: Headers[];
  currentViewHeaders: Headers[];
};

type MetricOptionsArgs = {
  experimentMetricsList: AnalyticsMetric[];
  metricTranslations: Record<string, string>;
  translations: ExperimentAnalyticsTranslation;
  messageMedium: MessageMedium;
  winningCriteriaName: string;
  campaignConversions: string[];
};

type generateBaseOptionsArgs = {
  /** List of metric names */
  keys: string[];
  /**
   * Identify where the data will be available in the row props
   *
   * MetricIdentifier.SYSTEM_METRICS     -> row.__metrics__
   * MetricIdentifier.CONVERSION         -> row.__conversion__
   * MetricIdentifier.UNIQUE_CONVERSION  -> row.__unique_conversion__
   * */
  identifier: MetricIdentifier;
  /** Groups metrics as sections for the dropdown */
  categoryId: MetricCategory;
  messageMedium: MessageMedium;
  metricTranslations?: Record<string, string>;
  translations?: ExperimentAnalyticsTranslation;
  winningCriteriaName?: string;
  winningCriteriaMetric?: string;
  sort?: boolean;
};

export const getWinningCriteriaMetric = (
  messageMedium: MessageMedium,
  winningCriteriaName: string,
  translations: ExperimentAnalyticsTranslation,
) =>
  metricsMediumWinningCriteriaDefaults?.[messageMedium]?.[winningCriteriaName] ||
  `${translations.metrics.unique} ${winningCriteriaName}`;

export const isCustomWinningCriteriaMetric = (
  messageMedium: MessageMedium,
  winningCriteriaName: string,
) => !metricsMediumWinningCriteriaDefaults?.[messageMedium]?.[winningCriteriaName];

export const getDefaultMetrics = (
  messageMedium: MessageMedium,
  winningCriteriaName: string,
  translations: ExperimentAnalyticsTranslation,
) => [
  ...new Set([
    ...(metricsMediumDefaults[messageMedium] || []),
    getWinningCriteriaMetric(messageMedium, winningCriteriaName, translations),
  ]),
];

const generateBaseOptions = ({
  keys,
  categoryId,
  identifier,
  messageMedium,
  metricTranslations,
  translations,
  winningCriteriaMetric,
  winningCriteriaName = '',
  sort = true,
}: generateBaseOptionsArgs): MetricOption<string>[] => {
  const options = keys?.map((key) => ({
    id: key,
    categoryId,
    label: metricTranslations?.[key] || key,
    value: key,
    // Marks the winning criteria in the dropdown
    ...(key === winningCriteriaMetric && {
      doubleLine: translations?.detailsMetadata?.winningCriteria,
    }),
    // The data identifier helps us to retrieve data for this particular metric
    data: {
      // This check lets us know to retrieve unique conversion data
      // from a different source in our row data
      identifier:
        key === winningCriteriaMetric &&
        isCustomWinningCriteriaMetric(messageMedium, winningCriteriaName)
          ? MetricIdentifier.UNIQUE_CONVERSION
          : identifier,
    },
  }));

  return sort ? options?.sort((a, b) => a.label.localeCompare(b.label)) : options;
};

export const getMetricOptions = ({
  messageMedium,
  winningCriteriaName,
  experimentMetricsList,
  metricTranslations,
  translations,
  campaignConversions,
}: MetricOptionsArgs): MetricOption<string>[] => {
  const winningCriteriaMetric = getWinningCriteriaMetric(
    messageMedium,
    winningCriteriaName,
    translations,
  );
  const defaultMetrics = getDefaultMetrics(messageMedium, winningCriteriaName, translations);
  // Filtering out the top level conversion event metric. Since, the individual custom event
  // metrics are available as terms inside the top level metric.
  const metricList = experimentMetricsList
    .map(({ metricName }) => metricName)
    .filter((metricName) => !CONVERSION_EVENT_METRICS.includes(metricName));

  /**
   * The default metric list would have items from the metrics and conversion events
   * So, we filter them out while displaying the additional and unique conversion events
   * to prevent any duplication of items in the dropdown
   */
  const additionalMetrics = metricList.filter((metric) => !defaultMetrics.includes(metric));
  const uniqueConversionMetrics = campaignConversions
    // Updating the events with the prefix 'Unique' before filtering.
    // Since, the winning winning criteria would be on the Unique conversion metric
    .map((event) => printUniqueMetricName(translations.metrics.unique, event))
    .filter((event) => !defaultMetrics.includes(event));

  return [
    // The default metrics
    ...generateBaseOptions({
      keys: defaultMetrics,
      categoryId: MetricCategory.DEFAULT,
      identifier: MetricIdentifier.SYSTEM_METRICS,
      messageMedium,
      winningCriteriaMetric,
      winningCriteriaName,
      metricTranslations,
      translations,
      sort: false,
    }),
    // The conversion metrics
    ...generateBaseOptions({
      keys: campaignConversions,
      categoryId: MetricCategory.CONVERSION,
      identifier: MetricIdentifier.CONVERSION,
      messageMedium,
    }),
    // The unique conversion metrics
    ...generateBaseOptions({
      keys: uniqueConversionMetrics,
      categoryId: MetricCategory.CONVERSION,
      identifier: MetricIdentifier.UNIQUE_CONVERSION,
      messageMedium,
      metricTranslations,
    }),
    // The additional metrics
    ...generateBaseOptions({
      keys: additionalMetrics,
      categoryId: MetricCategory.ADDITIONAL,
      identifier: MetricIdentifier.SYSTEM_METRICS,
      messageMedium,
      metricTranslations,
    }),
  ];
};

export const getTableDefaultAndOptionalExportHeaders = (
  translations: Record<string, string>,
  metricOptions: MetricOption<string>[],
): TableDefaultAndOptionalExportHeaders => {
  const defaultHeaders = Object.values(EXPORT_TABLE_DEFAULT_HEADER).map((header) => ({
    key: header,
    label: translations[header] || header,
  }));
  const optionalHeaders = metricOptions.map(({ id, label }) => ({
    key: id,
    label: (label || id).toLocaleString(),
  }));
  return {
    defaultHeaders,
    optionalHeaders,
  };
};

export const getTableDisplayHeaders = ({
  defaultHeaders,
  optionalHeaders,
  selectedMetricsColumn,
}: TableDisplayHeadersArgs): TableDisplayHeaders => ({
  allMetricsHeaders: [...defaultHeaders, ...optionalHeaders],
  currentViewHeaders: [
    ...defaultHeaders,
    ...optionalHeaders.filter(({ key }) => selectedMetricsColumn.includes(key)),
  ],
});

export const getVariantTypeText = (
  translations: ExperimentAnalyticsTranslation,
  control: boolean,
  winner: boolean,
) => {
  switch (true) {
    case control && winner:
      return `${translations.badge.control}, ${translations.badge.winner}`;

    case control && !winner:
      return translations.badge.control;

    case !control && winner:
      return translations.badge.winner;

    default:
      return '-';
  }
};

type getMetricExportDataProps = {
  row: ExperimentVariantBreakdownDetail | ExperimentHoldoutBreakdownDetail;
  metricMap: Record<string, MetricIdentifier>;
  metricOptions: MetricOption<string>[];
};

/**
 * Takes an array of metric options and
 * returns a metric data object for csv exporting.
 * Used in getTableExportData
 */
const getMetricExportData = ({ row, metricMap, metricOptions }: getMetricExportDataProps) => {
  const metrics = metricOptions.reduce((acc, { id: columnName }) => {
    const metricValue =
      retrieveMetricResult({
        dataStore: row,
        metricMap,
        metric: columnName,
      })?.value ?? NA;

    return {
      ...acc,
      [columnName]: typeof metricValue === 'number' ? localizeNumber(metricValue) : metricValue,
    };
  }, {});
  return metrics;
};

type getTableExportDataArgs = {
  rows: ExperimentVariantBreakdownArray;
  metricMap: Record<string, MetricIdentifier>;
  metricOptions: MetricOption<string>[];
  translations: ExperimentAnalyticsTranslation;
};

export const getTableExportData = ({
  rows,
  metricMap,
  metricOptions,
  translations,
}: getTableExportDataArgs): ExperimentTableDefaultExportData[] =>
  rows.map((row) => {
    if (isExperimentHoldoutRow(row)) {
      const holdoutRowExportData = getMetricExportData({ row, metricMap, metricOptions });
      return {
        [EXPORT_TABLE_DEFAULT_HEADER.VARIANT_NAME]: 'Holdout Group',
        ...holdoutRowExportData,
      };
    }
    const {
      campaignID,
      campaignName,
      variantTemplateID,
      testSubject,
      variantName,
      control,
      winner,
      content,
      sendTime,
      sendTimeUseRecipientTimeZones,
      messageMedium,
    } = row;
    const { origin } = window.location;
    const templateLink = `${origin}${getVariationTemplateLink(variantTemplateID)}`;

    return {
      [EXPORT_TABLE_DEFAULT_HEADER.CAMPAIGN_ID]: campaignID,
      [EXPORT_TABLE_DEFAULT_HEADER.CAMPAIGN_NAME]: campaignName,
      [EXPORT_TABLE_DEFAULT_HEADER.TEMPLATE_ID]: variantTemplateID,
      [EXPORT_TABLE_DEFAULT_HEADER.VARIANT_NAME]: variantName,
      [EXPORT_TABLE_DEFAULT_HEADER.VARIANT_LINK]: templateLink,
      [EXPORT_TABLE_DEFAULT_HEADER.VARIANT_STATUS]: getVariantTypeText(
        translations,
        control,
        winner,
      ),
      [EXPORT_TABLE_DEFAULT_HEADER.TEST_SUBJECT]:
        translations.experimentType[testSubject] || testSubject,
      [EXPORT_TABLE_DEFAULT_HEADER.TEST_SUBJECT_DETAILS]: getTestingSubjectText({
        formatted: false,
        content,
        sendTime,
        sendTimeUseRecipientTimeZones,
        messageMedium,
        control,
        type: testSubject,
        t: tFunctionWithSource(translations),
      }),
      ...getMetricExportData({ row, metricMap, metricOptions }),
    };
  });

const removeSpecialCharRegex = new RegExp(/[^a-zA-Z0-9_-]/, 'g');

export const getMetricExportFileName = (
  experimentId: number,
  experimentName: string,
  exportType: string,
) => {
  const expName = experimentName.replace(removeSpecialCharRegex, '_');
  const dateStamp = new Date().toLocaleString().replace(removeSpecialCharRegex, '_');

  return `iterable_experiment_${experimentId}_${expName}_${exportType}_report_${dateStamp}.csv`;
};

export const getTableExportingOptions = (t: TFunction) => [
  { label: t('variantTable.metrics.exportCurrent'), id: TABLE_EXPORT.CURRENT },
  { label: t('variantTable.metrics.exportAll'), id: TABLE_EXPORT.ALL },
];

export const getTableExportingActions = ({
  experimentId,
  experimentName,
  csvData,
  allMetricsHeaders,
  currentViewHeaders,
}: TableExportActionArgs) => ({
  [TABLE_EXPORT.CURRENT]: () => {
    const fileName = getMetricExportFileName(
      experimentId,
      experimentName,
      TABLE_EXPORT.CURRENT.replace('-', '_'),
    );
    return downloadAsCsvFileWithHeaders({ data: csvData, headers: currentViewHeaders, fileName });
  },
  [TABLE_EXPORT.ALL]: () => {
    const fileName = getMetricExportFileName(
      experimentId,
      experimentName,
      TABLE_EXPORT.ALL.replace('-', '_'),
    );
    return downloadAsCsvFileWithHeaders({ data: csvData, headers: allMetricsHeaders, fileName });
  },
});
